import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { generateLine, isHovering2 } from "./StraightLine";
import { pairs } from "d3-array";

import {
  isDefined,
  isNotDefined,
  noop,
  hexToRGBA,
  getStrokeDasharray,
} from "../../utils";

class GannFan extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }
  isHover(moreProps) {
    const { tolerance, onHover } = this.props;
    const { mouseXY } = moreProps;
    const [mouseX, mouseY] = mouseXY;

    let hovering = false;
    if (isDefined(onHover)) {
      const lines = helper(this.props, moreProps);

      for (let i = 0; i < lines.length; i++) {
        const line1 = lines[i];

        const left = Math.min(line1.x1, line1.x2);
        const right = Math.max(line1.x1, line1.x2);
        const top = Math.min(line1.y1, line1.y2);
        const bottom = Math.max(line1.y1, line1.y2);

        const isWithinLineBounds =
          mouseX >= left &&
          mouseX <= right &&
          mouseY >= top &&
          mouseY <= bottom;

        hovering =
          isWithinLineBounds &&
          isHovering2(
            [line1.x1, line1.y1],
            [line1.x2, line1.y2],
            mouseXY,
            tolerance,
          );

        if (hovering) break;
      }
    }
    return hovering;
  }
  drawOnCanvas(ctx, moreProps) {
    const {
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      fill,
      fillOpacity,
      fontFamily,
      fontSize,
      fontFill,
    } = this.props;

    const lines = helper(this.props, moreProps);

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = hexToRGBA(stroke, strokeOpacity);
    ctx.setLineDash(getStrokeDasharray(strokeDasharray).split(","));

    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = fontFill;

    lines.forEach((line) => {
      const { x1, y1, x2, y2, label } = line;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.beginPath();
      ctx.fillText(label.text, label.x, label.y);
    });
    const pairsOfLines = pairs(lines);

    // compute maximum radius among all line ends so radial gradients have a good scale
    const start = lines.length ? [lines[4].x1, lines[4].y1] : null; // middle entry should be the start
    const maxGlobalRadius = start
      ? Math.max(
          ...lines.map((l) => Math.hypot(l.x2 - start[0], l.y2 - start[1])),
        )
      : 0;

    pairsOfLines.forEach(([line1, line2], idx) => {
      const sx = line1.x1;
      const sy = line1.y1;
      const r1 = Math.hypot(line1.x2 - sx, line1.y2 - sy);
      const r2 = Math.hypot(line2.x2 - sx, line2.y2 - sy);
      const maxRadius = Math.max(r1, r2, maxGlobalRadius || 1);

      // support either an array of fills or a single fill object/string
      const rawFill = Array.isArray(fill) ? fill[idx] : fill;
      const color = rawFill ? rawFill : stroke;
      // Use fillOpacity as the maximum alpha for the gradient
      // Apply weighting to create variation between center and edge wedges
      const maxAlpha = typeof fillOpacity === "number" ? fillOpacity : 0.03;

      const pairsCount = pairsOfLines.length;
      const centerIndex = (pairsCount - 1) / 2; // e.g. 3.5 for 8 pairs
      const minWeight = 0.3; // minimum weight for outer wedges (30% of max alpha)
      let weight = 1;
      if (centerIndex > 0) {
        weight = Math.max(
          minWeight,
          1 - (Math.abs(idx - centerIndex) / centerIndex) * 0.7,
        );
      }

      // Calculate opacities: center starts at maxAlpha, edge fades to 20% of that
      const centerOpacity = maxAlpha * weight;
      const edgeOpacity = centerOpacity * 0.2;

      const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, maxRadius);
      grad.addColorStop(0, hexToRGBA(color, centerOpacity));
      grad.addColorStop(1, hexToRGBA(color, edgeOpacity));

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(line1.x2, line1.y2);
      ctx.lineTo(line2.x2, line2.y2);
      ctx.closePath();
      ctx.fill();
    });
  }
  renderSVG(moreProps) {
    const {
      stroke,
      strokeWidth,
      strokeOpacity,
      strokeDasharray,
      fill,
      fillOpacity,
    } = this.props;

    const lines = helper(this.props, moreProps);
    const pairsOfLines = pairs(lines);

    // compute maximum radius among all line ends so radial gradients have a good scale (match canvas)
    const start = lines.length ? [lines[4].x1, lines[4].y1] : null; // middle entry should be the start
    const maxGlobalRadius = start
      ? Math.max(
          ...lines.map((l) => Math.hypot(l.x2 - start[0], l.y2 - start[1])),
        )
      : 0;

    // create defs with radial gradients for each pair
    const defs = (
      <defs>
        {pairsOfLines.map(([line1, line2], idx) => {
          const sx = line1.x1;
          const sy = line1.y1;
          const r1 = Math.hypot(line1.x2 - sx, line1.y2 - sy);
          const r2 = Math.hypot(line2.x2 - sx, line2.y2 - sy);
          const maxRadius = Math.max(r1, r2, maxGlobalRadius || 1);
          // support either an array of fills or a single fill object/string
          const rawFill = Array.isArray(fill) ? fill[idx] : fill;
          const rawColor = rawFill ? rawFill : stroke;
          // normalize color for SVG: registry sometimes provides objects like { color: '#888' }
          const color =
            typeof rawColor === "object" &&
            rawColor != null &&
            typeof rawColor.color === "string"
              ? rawColor.color
              : rawColor;

          // Use fillOpacity as the maximum alpha for the gradient (matching canvas implementation)
          const maxAlpha = typeof fillOpacity === "number" ? fillOpacity : 0.06;

          const pairsCount = pairsOfLines.length;
          const centerIndex = (pairsCount - 1) / 2;
          const minWeight = 0.3; // minimum weight for outer wedges (30% of max alpha)
          let weight = 1;
          if (centerIndex > 0) {
            weight = Math.max(
              minWeight,
              1 - (Math.abs(idx - centerIndex) / centerIndex) * 0.7,
            );
          }

          // Calculate opacities: center starts at maxAlpha, edge fades to 20% of that
          const centerOpacity = maxAlpha * weight;
          const edgeOpacity = centerOpacity * 0.2;
          const gid = `gannfan-grad-${idx}-${Math.round(sx)}-${Math.round(sy)}`;
          return (
            <radialGradient
              key={gid}
              id={gid}
              cx={sx}
              cy={sy}
              r={maxRadius}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={color} stopOpacity={centerOpacity} />
              <stop offset="100%" stopColor={color} stopOpacity={edgeOpacity} />
            </radialGradient>
          );
        })}
      </defs>
    );

    return (
      <g>
        {defs}
        {lines.map((each, idx) => {
          const { x1, y1, x2, y2 } = each;
          return (
            <line
              key={idx}
              strokeWidth={strokeWidth}
              stroke={stroke}
              strokeOpacity={strokeOpacity}
              strokeDasharray={getStrokeDasharray(strokeDasharray)}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
            />
          );
        })}
        {pairsOfLines.map(([line1, line2], idx) => {
          const sx = line1.x1;
          const sy = line1.y1;
          const gid = `gannfan-grad-${idx}-${sx}-${sy}`;
          return (
            <path
              key={idx}
              d={`M ${line1.x1} ${line1.y1} L ${line1.x2} ${line1.y2} L ${line2.x2} ${line2.y2} Z`}
              fill={`url(#${gid})`}
              stroke="none"
            />
          );
        })}
      </g>
    );
  }
  render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover } =
      this.props;

    return (
      <GenericChartComponent
        isHover={this.isHover}
        svgDraw={this.renderSVG}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={["mousemove", "mouseleave", "pan", "drag", "xAxisZoom"]}
      />
    );
  }
}

function getLineCoordinates(start, endX, endY, text) {
  const end = [endX, endY];
  return {
    start,
    end,
    text,
  };
}

function helper(props, moreProps) {
  const { startXY, endXY } = props;

  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;
  if (isNotDefined(startXY) || isNotDefined(endXY)) {
    return [];
  }
  const [x1, y1] = startXY;
  const [x2, y2] = endXY;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx !== 0 && dy !== 0) {
    // console.log("modLine ->", startXY, modLine, dx1, dy1)
    const halfY = getLineCoordinates(startXY, x2, y1 + dy / 2, "2/1");
    const oneThirdY = getLineCoordinates(startXY, x2, y1 + dy / 3, "3/1");
    const oneFourthY = getLineCoordinates(startXY, x2, y1 + dy / 4, "4/1");
    const oneEighthY = getLineCoordinates(startXY, x2, y1 + dy / 8, "8/1");
    const halfX = getLineCoordinates(startXY, x1 + dx / 2, y2, "1/2");
    const oneThirdX = getLineCoordinates(startXY, x1 + dx / 3, y2, "1/3");
    const oneFourthX = getLineCoordinates(startXY, x1 + dx / 4, y2, "1/4");
    const oneEighthX = getLineCoordinates(startXY, x1 + dx / 8, y2, "1/8");
    const lines = [
      oneEighthX,
      oneFourthX,
      oneThirdX,
      halfX,
      { start: startXY, end: endXY, text: "1/1" },
      halfY,
      oneThirdY,
      oneFourthY,
      oneEighthY,
    ];
    const lineCoods = lines.map((line) => {
      const { x1, y1, x2, y2 } = generateLine({
        type: "RAY",
        start: line.start,
        end: line.end,
        xScale,
        yScale,
      });
      return {
        x1: xScale(x1),
        y1: yScale(y1),
        x2: xScale(x2),
        y2: yScale(y2),
        label: {
          x: xScale(line.end[0]),
          y: yScale(line.end[1]),
          text: line.text,
        },
      };
    });
    return lineCoods;
  }
  return [];
}

GannFan.propTypes = {
  interactiveCursorClass: PropTypes.string,
  stroke: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number.isRequired,
  strokeDasharray: PropTypes.string,
  strokeOpacity: PropTypes.number.isRequired,
  fill: PropTypes.arrayOf(PropTypes.string),
  fillOpacity: PropTypes.number,

  fontFamily: PropTypes.string.isRequired,
  fontSize: PropTypes.number.isRequired,
  fontFill: PropTypes.string.isRequired,

  onDragStart: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,

  defaultClassName: PropTypes.string,

  tolerance: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
};

GannFan.defaultProps = {
  onDragStart: noop,
  onDrag: noop,
  onDragComplete: noop,

  strokeWidth: 1,
  tolerance: 4,
  selected: false,
  fillOpacity: 0.01,
};

export default GannFan;
